// One scene, one root, and the subtrees that mount into it.
//
// The sim owned a `THREE.Scene` and the Atlas built a second one per visit, so
// opening the Solar System tab replaced the graph the frame was drawn from.
// Here there is one scene and one root that outlives every mount: the
// sim mounts its subtree at boot and the Atlas mounts an `atlas-subtree` in
// `renderAtlasTab` and removes it at dispose.
//
// Exactly one subtree DRAWS in any frame - `src/main.ts`'s tick hands the frame
// to the stage while the menu is open and draws its own composer only while it
// is closed - so `activate()` attaches the drawing side and detaches the
// others. Detached rather than `visible = false`: a hidden subtree is still
// walked by `Object3D.traverse`, which is how the instruments enumerate what a
// frame can reach, and it still spends slots in the light state. Round 9's land
// verb, which wants the ship and the terrain in one frame, is what changes
// that; until then a detached subtree is what the two sides looked like when
// they were two scenes.

import * as THREE from 'three';
import { createWorldFrame, type WorldFrameHandle } from './frame.ts';

/** Scene-level state that belongs to one subtree and is applied when it draws. */
export interface SubtreeSceneState {
  background: THREE.Scene['background'];
  environment: THREE.Scene['environment'];
  environmentIntensity: number;
  fog: THREE.Scene['fog'];
}

export interface SubtreeHandle {
  /** `sim`, `atlas`: the group is `<label>-subtree` and the frame `<label>-world-root`. */
  readonly label: string;
  /** Everything this side draws hangs off here. */
  readonly group: THREE.Group;
  /** Rooted inside the subtree, so each side keeps its own reference point. */
  readonly frame: WorldFrameHandle;
  /** What the composer's RenderPass follows while this subtree is the active one. */
  camera: THREE.Camera | null;
  /** Merged over this subtree's own scene state, and applied now if it is active. */
  setSceneState(patch: Partial<SubtreeSceneState>): void;
  /**
   * Stay attached while another subtree is the one drawing.
   *
   * The Atlas is the world the sim flies in, so in cruise its bodies have to be
   * in the frame the sim's own composer draws. The scene state still belongs to
   * whichever side is active, and `activate()` on this handle still detaches
   * every side that is not co-drawn.
   */
  setCoDraw(on: boolean): void;
  /** Attach this subtree, detach the others, apply its scene state. Idempotent. */
  activate(): void;
  /** Off the root. Everything under the group is the caller's to free. */
  dispose(): void;
}

export interface WorldSceneHandle {
  readonly scene: THREE.Scene;
  /** Never replaced for the lifetime of the page. */
  readonly root: THREE.Group;
  mount(label: string): SubtreeHandle;
  active(): SubtreeHandle | null;
  activeCamera(): THREE.Camera | null;
}

/** The name `tools/preview/descent-dojo.mjs --ship` reads the root back by. */
export const WORLD_ROOT_NAME = 'world-root';

/**
 * Materials three keys on this scene's light counts that read no light.
 *
 * A `SpriteMaterial` or an unlit `ShaderMaterial` never relinks itself when the
 * light state moves - three's `materialNeedsLights` is false for it - so it
 * keeps a program keyed on whatever the count was when it first drew. That
 * costs nothing until a SECOND program for the same material is acquired at a
 * different count: a per-shot clone of a staged prototype, or one of the
 * Atlas's own `renderer.compile` warm passes. The frame that links it is a
 * hitch, and `--lights` reads it as a mid-fight recompile.
 *
 * One scene means one set. The fight's light pool and the orrery's sun both
 * land in it, and the orrery's arrives whenever the body registry's fetch
 * does, so relinking at the pool alone left the prototypes keyed one light
 * short about half the time. Everything that adds or removes a light here
 * calls `relinkLightKeyed()` instead.
 */
const lightKeyed = new Set<THREE.Material>();

/** Track one. The returned function untracks it; call it from the owner's dispose. */
export function trackLightKeyed(material: THREE.Material): () => void {
  lightKeyed.add(material);
  return () => { lightKeyed.delete(material); };
}

/** Rekey every tracked material on the light counts the scene holds now. */
export function relinkLightKeyed(): void {
  for (const m of lightKeyed) m.needsUpdate = true;
}

let shared: WorldSceneHandle | null = null;

function createWorldScene(): WorldSceneHandle {
  const scene = new THREE.Scene();
  scene.name = 'world-scene';
  const root = new THREE.Group();
  root.name = WORLD_ROOT_NAME;
  scene.add(root);

  const mounted = new Set<SubtreeHandle>();
  /** Subtrees that stay attached while another side draws. */
  const coDrawn = new Set<SubtreeHandle>();
  let active: SubtreeHandle | null = null;
  /** Mount order, so a re-attach cannot reorder the root's children. */
  const order = new Map<THREE.Object3D, number>();
  let nextOrder = 0;

  function mount(label: string): SubtreeHandle {
    const group = new THREE.Group();
    group.name = `${label}-subtree`;
    const frameRoot = new THREE.Group();
    frameRoot.name = `${label}-world-root`;
    group.add(frameRoot);
    const frame = createWorldFrame({ root: frameRoot, label });
    const state: SubtreeSceneState = {
      background: null,
      environment: null,
      environmentIntensity: 1,
      fog: null,
    };
    const handle: SubtreeHandle = {
      label,
      group,
      frame,
      camera: null,
      setSceneState(patch: Partial<SubtreeSceneState>): void {
        Object.assign(state, patch);
        if (active === handle) apply(state);
      },
      setCoDraw(on: boolean): void {
        if (on) coDrawn.add(handle);
        else coDrawn.delete(handle);
        if (on && group.parent !== root) {
          root.add(group);
          sortChildren();
        }
      },
      activate(): void {
        if (active === handle) {
          if (group.parent !== root) { root.add(group); sortChildren(); }
          return;
        }
        for (const other of mounted) {
          if (other !== handle && !coDrawn.has(other) && other.group.parent === root) {
            root.remove(other.group);
          }
        }
        if (group.parent !== root) root.add(group);
        // `tools/preview/descent-dojo.mjs --ship` reads which side drew off the
        // FIRST `-subtree` child of the root, so a side coming back must come
        // back where it was rather than after the one that stayed.
        sortChildren();
        apply(state);
        active = handle;
      },
      dispose(): void {
        mounted.delete(handle);
        coDrawn.delete(handle);
        order.delete(group);
        if (group.parent === root) root.remove(group);
        group.remove(frameRoot);
        frame.dispose();
        // The next activate() has to reapply, or the side that comes back draws
        // under the scene state this one left behind.
        if (active === handle) active = null;
      },
    };
    mounted.add(handle);
    order.set(group, nextOrder++);
    return handle;
  }

  function sortChildren(): void {
    root.children.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  }

  function apply(state: SubtreeSceneState): void {
    scene.background = state.background;
    scene.environment = state.environment;
    scene.environmentIntensity = state.environmentIntensity;
    scene.fog = state.fog;
  }

  return {
    scene,
    root,
    mount,
    active(): SubtreeHandle | null {
      return active;
    },
    activeCamera(): THREE.Camera | null {
      return active ? active.camera : null;
    },
  };
}

/**
 * The page's one scene.
 *
 * A module singleton and not a `createX()` the way everything else here is: the
 * sim and the Atlas must reach the SAME root without importing each other, and
 * `main.ts` is not importable from `src/atlas.ts` (it would be circular). This
 * is the same handoff `stage.ts` does for the renderer, minus the setter,
 * because a scene needs nothing from the boot order to exist.
 */
export function worldScene(): WorldSceneHandle {
  if (!shared) shared = createWorldScene();
  return shared;
}
